import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Clock, ArrowRight } from 'lucide-react';

const ThankYouPage = () => {
  return (
    <main>
      <Helmet>
        <title>Thank You | ComfortMaster Heating &amp; Air</title>
        <meta name="description" content="Thank you for contacting ComfortMaster Heating &amp; Air. We have received your request and will be in touch shortly." />
        <meta name="robots" content="noindex, nofollow" />
        
        {/* Event snippet for Submit lead form conversion page */}
        <script>{`
          gtag('event', 'conversion', {
            'send_to': 'AW-17258441081/Gw92CL3L8OEaEPnSu6VA',
            'value': 1.0,
            'currency': 'USD'
          });
        `}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">
            Thank You!
          </h1>
          <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
            Your request has been received. A member of our team will contact you shortly.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* What Happens Next */}
            <div className="bg-stone-50 rounded-2xl p-8">
              <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
                What Happens Next?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Review Your Request</p>
                    <p className="text-slate-600 text-sm">Our team will review your information and service needs.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">We Will Contact You</p>
                    <p className="text-slate-600 text-sm">Expect a call or email within 1 business day.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Schedule Your Service</p>
                    <p className="text-slate-600 text-sm">We will work with you to find a convenient time.</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Need Immediate Help */}
            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <h2 className="font-heading font-bold text-2xl mb-6">
                Need Immediate Help?
              </h2>
              <p className="text-stone-300 mb-6">
                For urgent HVAC emergencies, do not wait - give us a call and we will dispatch a technician right away.
              </p>
              <a 
                href="tel:8593576803" 
                className="flex items-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 px-6 rounded-xl transition-colors mb-6"
              >
                <Phone size={24} />
                <div>
                  <p className="text-sm text-emerald-200">Call Us Now</p>
                  <p className="text-xl">(859) 357-6803</p>
                </div>
              </a>
              <div className="flex items-center gap-2 text-stone-400 text-sm">
                <Clock size={16} />
                <span>24/7 Emergency Service Available</span>
              </div>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors"
            >
              Return to Homepage
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 bg-stone-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 mb-4">Trusted by Central Kentucky homeowners for over 25 years</p>
          <div className="flex flex-wrap justify-center gap-8 text-slate-400">
            <span>✓ Licensed & Insured</span>
            <span>✓ Same-Day Service</span>
            <span>✓ Free Second Opinions</span>
            <span>✓ We Stand By Our Work</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ThankYouPage;
